import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const getCompressedImageUrl = (img) => {
  if (!img) return "";
  if (typeof img === "object") {
    if (img.compressed_url) return img.compressed_url;
    if (img.compressed && typeof img.compressed === "string") return img.compressed;
    if (img.compressed_image) return img.compressed_image;
    if (img.url) return getCompressedImageUrl(img.url);
    return "";
  }
  if (typeof img === "string") {
    if (img.includes("compressed_")) return img;
    const lastSlash = img.lastIndexOf("/");
    if (lastSlash !== -1) {
      const dir = img.substring(0, lastSlash + 1);
      const filename = img.substring(lastSlash + 1);
      const dot = filename.lastIndexOf(".");
      const nameWithoutExt = dot !== -1 ? filename.substring(0, dot) : filename;
      return `${dir}compressed_${nameWithoutExt}.webp`;
    }
    return img;
  }
  return "";
};

const IconTooltip = ({ label, align = "center", children }) => {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      {children}
      {show && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            ...(align === "left" ? { left: 0 } : { left: "50%", transform: "translateX(-50%)" }),
            whiteSpace: "nowrap",
            background: "#18181B",
            color: "#fff",
            fontSize: "11px",
            fontWeight: 500,
            lineHeight: 1.2,
            padding: "4px 8px",
            borderRadius: "6px",
            pointerEvents: "none",
            zIndex: 50,
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
};

function Card({
  images,
  compressedImage,
  originalImage,
  title,
  subtitle,
  time,
  live,
  views,
  category,
  categoryIcon,
  button,
  buttonDisabled,
  buttonTitle,
  secondaryButton,
  secondaryButtonDisabled,
  secondaryButtonTitle,
  buttonReminder,
  date,
  like,
  likeDelete,
  dateTime,
  price,
  isAskForPrice,
  editDelete,
  onClick,
  onButtonClick,
  onSecondaryButtonClick,
  onEdit,
  onDelete,
  onReminderClick,
  isLiked,
  onLikeClick,
  distance,
  isPublished,
  onPublish
}) {
  const { t } = useTranslation();
  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hrs === 0) {
      return `${mins} ${t("common.min")}`;
    }

    if (mins === 0) {
      return `${hrs} ${t("common.hour")}`;
    }

    return `${hrs} ${t("common.hour")} ${mins} ${t("common.min")}`;
  };
  const isVideoUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    return /\.(mp4|webm|mov|avi|mkv|m4v|ogv)(\?.*)?$/i.test(url);
  };
  const isVid = isVideoUrl(images) || isVideoUrl(originalImage);

  const primaryMedia = compressedImage || (typeof images === "string" && images.includes("compressed_") ? images : getCompressedImageUrl(images)) || images || originalImage || '/images/card1.png';
  const fallbackMedia = originalImage || (typeof images === "string" ? images : (typeof images === "object" ? images?.url : "")) || '/images/card1.png';

  return (
    <div className="bg-white rounded-xl shadow-[0px_1px_5px_rgba(0,0,0,0.1)] w-full h-full hover:shadow-lg transition-shadow duration-300 mb-2">
      <div onClick={onClick} className="relative cursor-pointer overflow-hidden rounded-t-xl">
        {isVid ? (
          <div className="relative w-full h-[160px] sm:h-[200px] md:h-[220px] bg-black">
            <video
              src={images || originalImage}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              autoPlay
            />
            <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 backdrop-blur-sm z-10">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-red-500">
                <path fillRule="evenodd" d="M2 10a8 8 0 1116 0 8 8 0 01-16 0zm6.39-2.901A1 1 0 007 8v4a1 1 0 001.39.911l4-2a1 1 0 000-1.822l-4-2z" clipRule="evenodd" />
              </svg>
              VIDEO
            </div>
          </div>
        ) : (
          <img
            src={primaryMedia}
            onError={(e) => {
              if (fallbackMedia && e.target.src !== fallbackMedia) {
                e.target.src = fallbackMedia;
              } else {
                e.target.onerror = null;
                e.target.src = '/images/card1.png';
              }
            }}
            alt={title || "Product Image"}
            className="w-full aspect-[300/220] object-cover rounded-t-xl bg-gray-100"
          />
        )}
        {live && <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full z-10">{t("common.live")}</span>}

        {onLikeClick && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onLikeClick(e);
            }}
            className={`absolute top-2 right-2 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 transition-all active:scale-90 z-[20] cursor-pointer shadow-sm ${
              isLiked
                ? "bg-pink-500 text-white shadow-pink-200"
                : "bg-white/90 backdrop-blur-sm text-zinc-600 hover:text-pink-500 hover:bg-white border border-black/5"
            }`}
            title={isLiked ? t("common.unfavourite", { defaultValue: "Remove from favourites" }) : t("common.favourite", { defaultValue: "Add to favourites" })}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill={isLiked ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={isLiked ? "0" : "1.75"}
              className="w-5 h-5 transition-transform"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
              />
            </svg>
          </button>
        )}

        {like && (
          <span className="absolute top-2 right-2">
            <img src="/images/like.png" alt="like" />
          </span>
        )}

        {/* {time && <span className="absolute bottom-2 left-2 bg-secondary-500 text-white text-xs font-semibold px-2 py-1 rounded-full">{formatDuration(time)}</span>} */}
      </div>
      <div className="p-[15px] flex justify-between items-start gap-[10px]">
        <div className="flex flex-col gap-[5px] w-full overflow-hidden">
          <h3 onClick={onClick} className={`heading-web-h5 text-zinc-950 truncate w-full ${onClick ? 'cursor-pointer' : ''}`} title={title}>
            {title}
          </h3>

          {isAskForPrice ? (
            <h3 className="heading-web-h5 text-zinc-950">{t("ask_to_seller")}</h3>
          ) : price ? (
            <h3 className="heading-web-h5 text-zinc-950">INR {price}</h3>
          ) : null}
          {dateTime && (
            <p className="flex gap-[5px] items-center">
              <img src="/images/cal1.png" alt="eye" />
              <span className="body-md text-zinc-500"> {dateTime || "July 25, 2025 at 3:00 PM"}</span>
            </p>
          )}
          <p className="body-md text-zinc-500 truncate w-full" title={subtitle}>{subtitle}</p>
          {(distance !== undefined && distance !== null) && (
            <p className="flex gap-[5px] items-center">
              <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                {distance < 0.1 ? t("common.nearby", "Nearby") : `${Number(distance).toFixed(1)} km`}
              </span>
            </p>
          )}
          {views && (
            <p className="flex gap-[5px] items-center">
              <img src="/images/eye1.png" alt="eye" />
              <span className="body-md text-zinc-500">1.2K {t("common.views")}</span>
            </p>
          )}
          {category && (
            <p className="body-md text-zinc-500 truncate w-full flex items-center gap-1.5">
              <img
                src={categoryIcon || "/category.png"}
                alt=""
                className="w-4 h-4 rounded object-cover flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/category.png";
                }}
              />
              <span className="truncate">{category}</span>
            </p>
          )}
          {date && <p className="body-md text-zinc-500 truncate w-full">{date}</p>}
          {(button || secondaryButton) && (
            <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 mt-2 w-full">
              {button && (
                <button
                  disabled={buttonDisabled}
                  title={buttonTitle}
                  className="flex-1 min-w-[85px] h-[36px] px-3 btn-pri text-white border border-[#FF63B5] hover:bg-pink-600 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (buttonDisabled) return;
                    if (onButtonClick) onButtonClick(e);
                    else if (onClick) onClick(e);
                  }}
                >
                  {button}
                </button>
              )}
              {secondaryButton && (
                <button
                  disabled={secondaryButtonDisabled}
                  title={secondaryButtonTitle}
                  className="border border-[#FF63B5] text-[#FF63B5] hover:bg-pink-50 font-medium px-3.5 py-2 rounded-lg text-sm transition min-w-[105px] h-auto disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (secondaryButtonDisabled) return;
                    if (onSecondaryButtonClick) onSecondaryButtonClick(e);
                  }}
                >
                  {secondaryButton}
                </button>
              )}
            </div>
          )}
          {buttonReminder && (
            <button className="text-link !text-[#FF7E79] body-md mt-2 w-[fit-content]" onClick={(e) => {
              e.stopPropagation();
              if (onReminderClick) onReminderClick(e);
            }}>
              {buttonReminder}
            </button>
          )}
        </div>

        {likeDelete && (
          <div className="heart-delete-box flex gap-[5px] items-center">
            <button className="w-[34px] h-[34px] rounded-full flex items-center justify-center bg-secondary-500">
              <img src="/images/heart.svg" alt="heart" className="w-[18px]" />
            </button>
            <button className="w-[34px] h-[34px] rounded-full flex items-center justify-center bg-primary-400">
              <img src="/images/delete.svg" alt="delete" className="w-[18px]" />
            </button>
          </div>
        )}

      </div>
      {editDelete && (
        <div className="edit-delete-box flex items-center pl-[7px] pr-[15px] pb-[10px] -mt-[8px]" style={{ gap: "12px" }} onClick={(e) => e.stopPropagation()}>
          {onEdit && (
            <IconTooltip label={t("btn_edit")} align="left">
              <button
                type="button"
                aria-label={t("btn_edit")}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(e);
                }}
                className="w-[32px] h-[32px] rounded-full flex items-center justify-center hover:bg-zinc-100 transition cursor-pointer"
              >
                <img src="/images/edit.svg" alt="edit" className="w-[16px]" />
              </button>
            </IconTooltip>
          )}
          {onPublish && (
            <IconTooltip label={isPublished ? "Unmark as Published" : "Mark as Published"}>
              <button
                type="button"
                aria-label={isPublished ? "Unmark as Published" : "Mark as Published"}
                onClick={(e) => {
                  e.stopPropagation();
                  onPublish(e);
                }}
                className="w-[32px] h-[32px] rounded-full flex items-center justify-center hover:bg-zinc-100 transition cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-[16px] h-[16px] text-zinc-800">
                  {isPublished ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </>
                  )}
                </svg>
              </button>
            </IconTooltip>
          )}
          {onDelete && (
            <IconTooltip label={t("btn_delete")}>
              <button
                type="button"
                aria-label={t("btn_delete")}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(e);
                }}
                className="w-[32px] h-[32px] rounded-full flex items-center justify-center hover:bg-zinc-100 transition cursor-pointer"
              >
                <img src="/images/trash.svg" alt="trash" className="w-[18px]" />
              </button>
            </IconTooltip>
          )}
        </div>
      )}
    </div>
  );
}

export default Card;